import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    NavigateBefore as NavigateBeforeIcon,
    NavigateNext as NavigateNextIcon
} from '@mui/icons-material';
import { format, startOfYear } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { getYearTimecards } from '../../../services/timecardService';
import { useAuth } from '../../../context/AuthContext';
import ExportButton from '../../../components/common/ExportButton';

// Utility functions
const getLocalYearStart = (date) => {
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return startOfYear(localDate);
};

// Sticky column styles
const stickyColumnStyles = {
    project: {
        position: 'sticky',
        left: 0,
        zIndex: 3,
        backgroundColor: 'background.paper',
        minWidth: '300px',
        whiteSpace: 'nowrap',
        borderRight: '2px solid rgba(224, 224, 224, 1)', // Thicker border as only sticky column
        padding: '16px',
        '@media (max-width:600px)': {
            minWidth: '50%', // 50% of table width for mobile portrait
            maxWidth: '50%', // Prevent it from growing beyond 50%
            width: '50%', // Fixed width at 50%
            padding: '8px',
            fontSize: '0.75rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        }
    }
};

const YearlyProjectsViewAll = ({ onBack }) => {
    const [dataLoading, setDataLoading] = useState(false);
    const [error, setError] = useState(null);
    const [timecards, setTimecards] = useState([]);
    const [currentDate, setCurrentDate] = useState(new Date());
    const { user, loading: authLoading } = useAuth();

    // Memoize currentYearStart to prevent unnecessary recalculations
    const currentYearStart = useMemo(() => getLocalYearStart(currentDate), [currentDate]);

    // Fetch timecards when date changes
    useEffect(() => {
        let mounted = true;
        const controller = new AbortController();

        const fetchTimecardData = async () => {
            try {
                setDataLoading(true);
                const year = currentYearStart.getFullYear();
                
                console.log('Fetching timecards for all projects:', {
                    year,
                    userRole: user?.role
                });
                
                const data = await getYearTimecards(year, null, 'all');
                if (mounted) {
                    console.log('Raw timecard data received:', {
                        count: data.length,
                        year
                    });
                    
                    // Filter timecards based on user role
                    let filteredTimecards = [...data];
                    
                    // If user is a team lead, filter to only show projects from their teams
                    if (user.role === 'team_lead') {
                        console.log('User is a team lead, filtering timecards by project');
                        
                        // Get project IDs for the teams where the user is the team lead
                        let teamProjectIds = [];
                        
                        // Check if user has teams
                        if (user.teams && user.teams.length > 0) {
                            // Get only the teams where the user is the team lead
                            const leadTeams = user.teams.filter(team => 
                                String(team.teamLead) === String(user.id)
                            );
                            
                            if (leadTeams.length > 0) {
                                // Get all projects from these teams
                                const teamProjects = leadTeams.flatMap(team => team.projects || []);
                                
                                // Make sure we don't have duplicates
                                teamProjectIds = [...new Set(teamProjects.map(id => String(id)))];
                                
                                console.log('Team project IDs:', teamProjectIds);
                            }
                        }
                        
                        // If we have ledTeams, try to get projects from there
                        if (teamProjectIds.length === 0 && user.ledTeams && user.ledTeams.length > 0) {
                            console.log('Using ledTeams:', user.ledTeams);
                            
                            // Find the corresponding full teams for these ledTeams
                            const ledTeamIds = user.ledTeams.map(team => team._id);
                            
                            // Find the full team objects from the user.teams array
                            const fullLedTeams = user.teams ? user.teams.filter(team => 
                                ledTeamIds.some(id => String(id) === String(team._id))
                            ) : [];
                            
                            // Get all projects from these teams
                            const teamProjects = fullLedTeams.flatMap(team => team.projects || []);
                            
                            // Make sure we don't have duplicates
                            teamProjectIds = [...new Set(teamProjects.map(id => String(id)))];
                            
                            console.log('Team project IDs from ledTeams:', teamProjectIds);
                        }
                        
                        // Filter timecards to only show those for team projects
                        if (teamProjectIds.length > 0) {
                            filteredTimecards = filteredTimecards.filter(timecard => 
                                timecard.project && teamProjectIds.includes(String(timecard.project._id))
                            );
                            
                            console.log('Filtered timecards count:', filteredTimecards.length);
                        } else {
                            console.log('No team projects found, showing all timecards');
                        }
                    }
                    
                    // Group timecards by project and aggregate hours
                    const projectGroups = filteredTimecards.reduce((groups, timecard) => {
                        const projectName = timecard.project?.name || 'Unknown Project';
                        const projectId = timecard.project?._id || 'unknown';
                        const groupKey = projectId;

                        if (!groups[groupKey]) {
                            groups[groupKey] = {
                                _id: groupKey,
                                project: { name: projectName, _id: projectId },
                                months: Array(12).fill(null).map(() => ({ hours: 0 }))
                            };
                        }
                        
                        // Add hours from this timecard to the project group
                        timecard.months?.forEach((month, index) => {
                            if (month && month.hours) {
                                groups[groupKey].months[index].hours += parseFloat(month.hours);
                            }
                        });
                        
                        return groups;
                    }, {});

                    // Convert groups object to array
                    const aggregatedData = Object.values(projectGroups);

                    console.log('Timecards received and aggregated:', {
                        originalCount: data.length,
                        filteredCount: filteredTimecards.length,
                        aggregatedCount: aggregatedData.length,
                        year
                    });
                    
                    setTimecards(aggregatedData);
                    setError(null);
                }
            } catch (err) {
                if (err.name === 'AbortError') {
                    console.log('Fetch aborted');
                    return;
                }
                console.error('Error fetching timecards:', err);
                if (mounted) {
                    setError(err.message);
                    setTimecards([]);
                }
            } finally {
                if (mounted) {
                    setDataLoading(false);
                }
            }
        };

        // Only fetch if we have a user
        if (user) {
            fetchTimecardData();
        }

        return () => {
            mounted = false;
            controller.abort();
        };
    }, [currentYearStart, user]);

    const handlePreviousYear = () => {
        setCurrentDate(prev => {
            const newDate = new Date(prev);
            newDate.setFullYear(newDate.getFullYear() - 1);
            return newDate;
        });
    };

    const handleNextYear = () => {
        setCurrentDate(prev => {
            const newDate = new Date(prev);
            newDate.setFullYear(newDate.getFullYear() + 1);
            return newDate;
        });
    };


    // Memoize year info calculation
    const yearInfo = useMemo(() => {
        const months = Array.from({ length: 12 }, (_, i) => {
            const date = new Date(currentYearStart);
            date.setMonth(i);
            return {
                date: date.toISOString()
            };
        });
        
        return { months };
    }, [currentYearStart]);

    const { months } = yearInfo;

    // Show loading state while auth is loading
    if (authLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    // Show error if no user data
    if (!user) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <Alert severity="error">Authentication required</Alert>
            </Box>
        );
    }

    console.log('Rendering year for all projects:', {
        year: currentYearStart.getFullYear(),
        userRole: user.role
    });

    return (
        <Box sx={{ 
            p: 3,
            '@media (max-width:600px)': {
                p: 1, // Much more reduced padding for mobile
                paddingTop: 0, // No top padding - layout provides spacing
                paddingBottom: '88px', // Account for bottom navigation
            },
            '@media (max-width:900px) and (orientation: landscape)': {
                p: 0.5, // Minimal padding in landscape mode
                paddingTop: 0, // No top padding
                paddingBottom: 0.5, // Minimal bottom padding
                height: 'auto', // Allow natural height
                overflow: 'visible', // Allow content to overflow
                minHeight: 'auto', // Remove height constraints
            }
        }}>
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Header */}
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 3,
                '@media (max-width:600px)': {
                    mb: 1, // Much more reduced margin for mobile
                },
                '@media (max-width:900px) and (orientation: landscape)': {
                    mb: 0.5, // Reduced margin in landscape mode but keep header visible
                }
            }}>
                <IconButton onClick={onBack} sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6">
                    Yearly View - All Projects
                </Typography>
            </Box>

            {/* Navigation Bar */}
            <Paper sx={{ 
                p: 2, 
                mb: 3,
                '@media (max-width:600px)': {
                    p: 0, // Remove all padding on mobile
                    mb: 1.5,
                }
            }}>
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    '@media (max-width:600px)': {
                        flexDirection: 'row', // Keep on same line for mobile
                        gap: 0.5,
                        alignItems: 'center',
                        px: 0.5, // Add minimal padding inside the flex container
                        py: 1,
                    }
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        '@media (max-width:600px)': {
                            flex: 1,
                            gap: 0.5,
                            marginLeft: '-8px', // Move the entire year navigation widget to the left
                        }
                    }}>
                        <IconButton 
                            onClick={handlePreviousYear}
                            sx={{
                                '@media (max-width:600px)': {
                                    padding: '4px',
                                }
                            }}
                        >
                            <NavigateBeforeIcon />
                        </IconButton>
                        <Typography sx={{
                            '@media (max-width:600px)': {
                                fontSize: '0.75rem',
                                textAlign: 'center',
                                flex: 1,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }
                        }}>
                            {format(currentYearStart, 'yyyy', { locale: enUS })}
                        </Typography>
                        <IconButton 
                            onClick={handleNextYear}
                            sx={{
                                '@media (max-width:600px)': {
                                    padding: '4px',
                                }
                            }}
                        >
                            <NavigateNextIcon />
                        </IconButton>
                    </Box>
                    <ExportButton
                        viewType="yearly"
                        timecards={timecards}
                        isAllUsers={true}
                        viewMode="allProjects"
                        date={currentYearStart}
                        user={{ username: 'all_projects' }}
                        mobileIconOnly={true}
                    />
                </Box>
            </Paper>

            {/* Timecard Table */}
            <TableContainer 
                component={Paper}
                sx={{
                    overflow: 'auto',
                    '& .MuiTable-root': {
                        minWidth: months.length * 120 + 300, // Account for project column (300px) + months
                        borderCollapse: 'separate',
                        borderSpacing: 0
                    }
                }}
            >
                <Table size="small" stickyHeader>
                    <TableHead sx={{ bgcolor: '#e0e0e0' }}>
                        <TableRow>
                            <TableCell sx={stickyColumnStyles.project}>
                                Project
                            </TableCell>
                            {months.map((month, index) => {
                                const date = new Date(month.date);
                                return (
                                    <TableCell 
                                        key={index} 
                                        align="center"
                                        sx={{
                                            width: `${100 / months.length}%`,
                                            minWidth: '120px',
                                            padding: '16px',
                                            whiteSpace: 'nowrap',
                                            boxSizing: 'border-box'
                                        }}
                                    >
                                        {format(date, 'MMMM yyyy', { locale: enUS })}
                                    </TableCell>
                                );
                            })}
                            <TableCell 
                                align="center"
                                sx={{
                                    minWidth: '120px',
                                    padding: '16px'
                                }}
                            >
                                Total
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {timecards.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={months.length + 2} align="center">
                                    No timecard data found for this year
                                </TableCell>
                            </TableRow>
                        ) : (
                            timecards.map((timecard) => (
                                <TableRow key={timecard._id}>
                                    <TableCell sx={stickyColumnStyles.project}>
                                        {timecard.project?.name || 'Unknown Project'}
                                    </TableCell>
                                    {months.map((month, index) => {
                                        const monthData = timecard.months?.[index];
                                        return (
                                            <TableCell 
                                                key={index} 
                                                align="center"
                                                sx={{
                                                    minWidth: '120px',
                                                    padding: '16px'
                                                }}
                                            >
                                                {monthData ? parseFloat(monthData.hours).toFixed(2) : '0.00'}
                                            </TableCell>
                                        );
                                    })}
                                    <TableCell 
                                        align="center"
                                        sx={{
                                            minWidth: '120px',
                                            padding: '16px'
                                        }}
                                    >
                                        {(timecard.months || [])
                                            .reduce((sum, month) => sum + (month ? parseFloat(month.hours) : 0), 0)
                                            .toFixed(2)}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            
            {dataLoading && (
                <Box sx={{ 
                    position: 'absolute', 
                    top: 0, 
                    left: 0, 
                    right: 0, 
                    bottom: 0, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    zIndex: 1000
                }}>
                    <CircularProgress />
                </Box>
            )}
        </Box>
    );
};

export default YearlyProjectsViewAll;
